import {
  collectAssessmentData,
  getLastAssessmentResult,
} from "./assessment.ts";
import { state } from "../utils/lang.ts";
import { brandEmail } from "../utils/brand.ts";

// Initialize Get Advice functionality (PDF download first, then modal with mailto)
export function initGetAdvice(): void {
  const adviceBtn = document.getElementById("get-advice-btn");
  const modal = document.getElementById("advice-modal") as HTMLDialogElement;
  const adviceForm = document.getElementById("advice-form") as HTMLFormElement;
  const cancelBtn = document.getElementById("advice-cancel-btn");
  const companyNameInput = document.getElementById(
    "company-name",
  ) as HTMLInputElement;

  if (!adviceBtn || !modal) {
    console.warn(
      "Get Advice button or modal not found. Skipping initialization.",
    );
    return;
  }

  // Store assessment data for use in form submission
  let currentData: ReturnType<typeof collectAssessmentData> | null = null;

  // Click Get Advice: validate, download PDF, then open modal
  adviceBtn.addEventListener("click", async () => {
    const processNameInput = document.getElementById(
      "process-name",
    ) as HTMLInputElement;
    const processDescriptionInput = document.getElementById(
      "process-description",
    ) as HTMLTextAreaElement;

    if (!processNameInput?.value?.trim()) {
      processNameInput?.focus();
      processNameInput?.classList.add(
        "ring-2",
        "ring-red-500",
        "border-red-500",
      );
      setTimeout(() => {
        processNameInput?.classList.remove(
          "ring-2",
          "ring-red-500",
          "border-red-500",
        );
      }, 2000);
      return;
    }

    if (!processDescriptionInput?.value?.trim()) {
      processDescriptionInput?.focus();
      processDescriptionInput?.classList.add(
        "ring-2",
        "ring-red-500",
        "border-red-500",
      );
      setTimeout(() => {
        processDescriptionInput?.classList.remove(
          "ring-2",
          "ring-red-500",
          "border-red-500",
        );
      }, 2000);
      return;
    }

    currentData = collectAssessmentData();
    const assessmentResult = getLastAssessmentResult();
    const pdfPayload = assessmentResult
      ? { ...currentData, assessmentResult }
      : currentData;

    const defaultContent = document.getElementById("get-advice-btn-default");
    const loadingContent = document.getElementById("get-advice-btn-loading");

    // Download PDF first
    try {
      adviceBtn.classList.add("opacity-75", "cursor-wait");
      (adviceBtn as HTMLButtonElement).disabled = true;
      defaultContent?.classList.add("hidden");
      loadingContent?.classList.remove("hidden");

      const response = await fetch("/api/generate-pdf", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(pdfPayload),
      });

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${
        currentData.processName.replace(/[^a-zA-Z0-9]/g, "_")
      }_assessment.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Update placeholder for company name input based on current language
      if (companyNameInput) {
        const placeholderKey = `data-${state.lang}-placeholder`;
        const placeholder = companyNameInput.getAttribute(placeholderKey);
        if (placeholder) {
          companyNameInput.placeholder = placeholder;
        }
      }

      // Open modal after PDF download
      modal.showModal();
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      adviceBtn.classList.remove("opacity-75", "cursor-wait");
      (adviceBtn as HTMLButtonElement).disabled = false;
      defaultContent?.classList.remove("hidden");
      loadingContent?.classList.add("hidden");
    }
  });

  // Cancel button closes modal
  cancelBtn?.addEventListener("click", () => {
    modal.close();
  });

  // Close modal when clicking outside
  modal.addEventListener("click", (e) => {
    if (e.target === modal) {
      modal.close();
    }
  });

  // Handle form submission - opens mailto
  adviceForm?.addEventListener("submit", (e) => {
    e.preventDefault();

    const companyName = companyNameInput?.value?.trim();
    if (!currentData) return;

    // Multilingual email templates
    const adviceEmailTemplates = {
      en: {
        subject: "Advice needed",
        body: (description: string) =>
          `I need advice on how to optimize the following process: ${description}.

Please find attached the checklist I completed.`,
      },
      lv: {
        subject: "Nepieciešams padoms",
        body: (description: string) =>
          `Man nepieciešams padoms, kā optimizēt šādu procesu: ${description}.

Lūdzu, skatiet pievienoto kontrolsarakstu, ko es aizpildīju.`,
      },
      it: {
        subject: "Consulenza necessaria",
        body: (description: string) =>
          `Ho bisogno di consulenza su come ottimizzare il seguente processo: ${description}.

Si prega di trovare allegata la checklist che ho completato.`,
      },
    };

    const template = adviceEmailTemplates[
      state.lang as keyof typeof adviceEmailTemplates
    ] || adviceEmailTemplates.en;

    const emailSubject = companyName
      ? `${template.subject}: ${companyName}`
      : template.subject;
    const emailBody = template.body(
      currentData.description || currentData.processName,
    );

    const mailtoUrl = `mailto:${brandEmail("advice")}?subject=${
      encodeURIComponent(emailSubject)
    }&body=${encodeURIComponent(emailBody)}`;

    // Close modal and open email
    modal.close();
    globalThis.location.href = mailtoUrl;
  });
}
